import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@rickylandino/react-messages';
import { Table, Select, Modal } from 'antd';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import NumberFormat from 'react-number-format';
import Moment from 'moment';
import { ExternalLink, Trash2 } from 'lucide-react';
import Globals from '../../../../config/globals';
import { useFollowUpTrackingFiltered } from '../../../../hooks/useWidgetData';
import { useSaveWidgetFilters } from '../../../../hooks/useDashboard';
import PatientExamPane from '../../../Exams/PatientExamPane';
import { calculateAge } from '../../../Helpers/PatientHelper';
import { getPatientExamResults } from '../../../../common/services/ExamService';
import { deleteTrackingAction } from '../../../../common/services/next-gen/PatientTrackingService';
import { userAssociatedFacilities } from '../../../../common/services/ApiService';

const { Option } = Select;

/**
 * Pure helper � compute the unique sorted BIRADS list for a set of selected facility IDs.
 */
function computeAvailableBirads(facilityIds, allFacilities) {
    const ids = facilityIds.filter(f => f !== 'Select All');
    if (ids.length === 0) return [];

    const seen = new Set();
    const combined = [];
    ids.forEach(id => {
        const birads = allFacilities.find(f => f.facilityModel.facilityID === id)?.availableBirads || [];
        birads.forEach(b => {
            if (!seen.has(b.birads)) {
                seen.add(b.birads);
                combined.push(b);
            }
        });
    });
    return combined.sort((a, b) => a.birads.toUpperCase().localeCompare(b.birads.toUpperCase()));
}

/**
 * FollowUpTrackingFilteredWidget component
 * User-level widget showing follow-up tracking records filtered by date range, facility, and BIRADS.
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 * @param {Object} [props.widget] - Widget configuration object
 */
export default function FollowUpTrackingFilteredWidget({
    isCollapsed = false,
    isExpanded = false,
    setIsLoading,
    setSharedState,
    widget
}) {
    const saveFiltersMutation = useSaveWidgetFilters();
    const queryClient = useQueryClient();
    const isInitialLoad = useRef(true);
    const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));

    const [allFacilities, setAllFacilities] = useState([]);

    const [selectedDates, setSelectedDates] = useState({ startDate: null, endDate: null });
    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [availableBirads, setAvailableBirads] = useState([]);
    const [selectedBirads, setSelectedBirads] = useState([]);
    const [queryParams, setQueryParams] = useState(null);

    const [showAddExamPane, setShowAddExamPane] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedPatientExam, setSelectedPatientExam] = useState(null);
    const [examResults, setExamResults] = useState(null);
    const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
    const [facility, setFacility] = useState(null);

    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    // Parse saved filter config
    const savedFilters = useMemo(() => {
        if (!widget?.filterConfig) return null;
        if (typeof widget.filterConfig === 'string') {
            try { return JSON.parse(widget.filterConfig); } catch { return null; }
        }
        return widget.filterConfig;
    }, [widget?.filterConfig]);

    const shouldLoadData = !isCollapsed || isExpanded;

    const {
        data: results = [],
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useFollowUpTrackingFiltered(queryParams, shouldLoadData && queryParams !== null);

    // Notify parent of loading state
    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    // Show error toast
    useEffect(() => {
        if (shouldLoadData && isError) {
            toast.error(`Failed to load follow-up tracking: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadData, isError, error]);

    // Set refetch in shared state
    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    // Subscribe to facilities observable so the list populates correctly after a page refresh
    useEffect(() => {
        const subscription = userAssociatedFacilities.subscribe(facilities => {
            if (facilities?.length > 0) {
                setAllFacilities(facilities);
            }
        });
        return () => subscription.unsubscribe();
    }, []);

    // Restore saved filters on initial load, or auto-select if only one facility
    useEffect(() => {
        if (allFacilities.length === 0 || !isInitialLoad.current) return;
        isInitialLoad.current = false;

        if (savedFilters) {
            if (savedFilters.startDate || savedFilters.endDate) {
                setSelectedDates({
                    startDate: savedFilters.startDate ? Moment(savedFilters.startDate).toDate() : null,
                    endDate: savedFilters.endDate ? Moment(savedFilters.endDate).toDate() : null
                });
            }
            const facilitiesToRestore = savedFilters.facilityIds?.length > 0
                ? savedFilters.facilityIds
                : allFacilities.length === 1 ? [allFacilities[0].facilityModel.facilityID] : [];

            if (facilitiesToRestore.length > 0) {
                setSelectedFacilities(facilitiesToRestore);
                setAvailableBirads(computeAvailableBirads(facilitiesToRestore, allFacilities));
            }
            if (savedFilters.biradsValues?.length > 0) {
                setSelectedBirads(savedFilters.biradsValues);
            }
        } else if (allFacilities.length === 1) {
            const id = allFacilities[0].facilityModel.facilityID;
            setSelectedFacilities([id]);
            setAvailableBirads(computeAvailableBirads([id], allFacilities));
        }
    }, [savedFilters, allFacilities]);

    const handleNewDateChange = useCallback((name, date) => {
        setSelectedDates(prev => ({ ...prev, [name]: date }));
    }, []);

    const handleFacilityChange = useCallback((values) => {
        const allIds = allFacilities.map(f => f.facilityModel.facilityID);

        let next;
        if (values.includes('Select All') && !selectedFacilities.includes('Select All')) {
            next = allIds;
        } else if (!values.includes('Select All') && selectedFacilities.includes('Select All')) {
            next = [];
        } else if (selectedFacilities.includes('Select All') && values.includes('Select All')) {
            next = values.filter(f => f !== 'Select All');
        } else {
            const hasAll = allIds.length > 0 && allIds.every(id => values.includes(id));
            next = hasAll && allIds.length === values.length ? ['Select All', ...values] : values;
        }

        setSelectedFacilities(next);
        setAvailableBirads(computeAvailableBirads(next, allFacilities));
        setSelectedBirads([]);
    }, [selectedFacilities, allFacilities]);

    const handleBiradChange = useCallback((values) => {
        const allBirads = availableBirads.map(b => b.birads);

        if (values.includes('Select All') && !selectedBirads.includes('Select All')) {
            setSelectedBirads(['Select All', ...allBirads]);
        } else if (!values.includes('Select All') && selectedBirads.includes('Select All')) {
            setSelectedBirads([]);
        } else if (selectedBirads.includes('Select All') && values.includes('Select All')) {
            setSelectedBirads(values.filter(b => b !== 'Select All'));
        } else {
            const hasAll = allBirads.length > 0 && allBirads.every(b => values.includes(b));
            setSelectedBirads(hasAll && allBirads.length === values.length ? ['Select All', ...values] : values);
        }
    }, [selectedBirads, availableBirads]);

    const handleSubmit = useCallback((e) => {
        e?.preventDefault();

        const facilitiesValue = selectedFacilities.filter(f => f !== 'Select All');
        const biradsValue = selectedBirads.filter(b => b !== 'Select All');

        if (facilitiesValue.length === 0 || biradsValue.length === 0) {
            toast.error('Please select at least one facility and one BIRADS value.');
            return;
        }

        const startDate = selectedDates.startDate ? Moment(selectedDates.startDate).format('YYYY-MM-DD') : null;
        const endDate = selectedDates.endDate ? Moment(selectedDates.endDate).format('YYYY-MM-DD') : null;

        const params = {
            startDate: startDate || '',
            endDate: endDate || '',
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            selectedFacilities: facilitiesValue,
            selectedBirads: biradsValue
        };

        if (widget?.userDashboardWidgetId) {
            saveFiltersMutation.mutate({
                userDashboardWidgetId: widget.userDashboardWidgetId,
                filterConfig: { startDate, endDate, facilityIds: facilitiesValue, biradsValues: biradsValue }
            });
        }

        setQueryParams(params);
    }, [selectedDates, selectedFacilities, selectedBirads, widget?.userDashboardWidgetId, saveFiltersMutation]);

    const handleViewExam = useCallback(async (row) => {
        try {
            const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));

            if (!userInfo?.userId) {
                toast.error('User information not found. Please log in again.');
                return;
            }

            if (!row.patientExamId) {
                toast.error('Missing exam information');
                return;
            }

            if (!row.facilityId) {
                toast.warning('Facility information missing from API. Please contact support.');
                console.error('Missing facilityId in API response. Row:', row);
                return;
            }

            const postdata = {
                uniqueID: row.patientExamId,
                facilityID: row.facilityId,
                name: row.examClass,
                userID: userInfo.userId
            };

            const data = await getPatientExamResults(postdata);

            if (!data) {
                throw new Error('No data returned from server');
            }

            setSelectedPatient({ dob: row.dob || data.patientDob });
            setSelectedPatientExam(data.examInfo);
            setExamResults(data.resultInfo);
            setSharedFollowUpIDs(data.examIDs);
            setFacility({ facilityID: row.facilityId, facilityName: row.facilityName });
            setShowAddExamPane(true);

        } catch (error) {
            console.error('Error loading exam:', error);
            toast.error(`Failed to load exam: ${error?.message || 'Unknown error'}`);
        }
    }, []);

    const handleDeleteTracking = useCallback((row) => {
        if (!row.mostRecentTrackingActionId) {
            toast.warning('Unable to delete: Tracking action ID missing from API response');
            console.error('Missing mostRecentTrackingActionId in API response. Row:', row);
            return;
        }
        setRecordToDelete(row);
        setShowDeletionModal(true);
    }, []);

    const closeDeletionModal = useCallback(() => {
        setShowDeletionModal(false);
        setRecordToDelete(null);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        try {
            if (!recordToDelete?.mostRecentTrackingActionId) {
                toast.error('Missing tracking action ID');
                return;
            }
            await deleteTrackingAction(recordToDelete.mostRecentTrackingActionId);
            closeDeletionModal();
            queryClient.invalidateQueries({ queryKey: ['widgetData', 'follow-up-tracking-filtered'] });
            toast.success('Tracking action deleted successfully');
        } catch (error) {
            console.error('Error deleting tracking action:', error);
            toast.error(`Failed to delete: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
        }
    }, [recordToDelete, queryClient, closeDeletionModal]);

    const updateExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy('updating');
        setShowAddExamPane(false);
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'follow-up-tracking-filtered'] });
        toast.success('Widget data refreshed');
    }, [queryClient]);

    const getNewExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy('updating');
        setShowAddExamPane(false);
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'follow-up-tracking-filtered'] });
        toast.success('Widget data refreshed');
    }, [queryClient]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: 'numeric'
        });
    }, []);

    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            sorter: (a, b) => (a.patientName || '').localeCompare(b.patientName || ''),
            width: 180,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'DOB',
            dataIndex: 'dob',
            key: 'dob',
            render: (dob) => formatDate(dob),
            sorter: (a, b) => new Date(a.dob) - new Date(b.dob),
            width: 110,
        },
        {
            title: 'Referring Doc',
            dataIndex: 'referringPhysician',
            key: 'referringPhysician',
            sorter: (a, b) => (a.referringPhysician || '').localeCompare(b.referringPhysician || ''),
            ellipsis: true,
            width: 160,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            sorter: (a, b) => (a.facilityName || '').localeCompare(b.facilityName || ''),
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDate',
            key: 'examDate',
            render: (examDate) => formatDate(examDate),
            sorter: (a, b) => new Date(a.examDate) - new Date(b.examDate),
            defaultSortOrder: 'ascend',
            width: 110,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            sorter: (a, b) => (a.examType || '').localeCompare(b.examType || ''),
            ellipsis: true,
            width: 130,
        },
        {
            title: 'Result Date',
            dataIndex: 'mostRecentResultDate',
            key: 'mostRecentResultDate',
            render: (resultDate) => formatDate(resultDate),
            sorter: (a, b) => new Date(a.mostRecentResultDate) - new Date(b.mostRecentResultDate),
            width: 110,
        },
        {
            title: 'BIRADS',
            dataIndex: 'mostRecentBirads',
            key: 'mostRecentBirads',
            sorter: (a, b) => (a.mostRecentBirads || '').localeCompare(b.mostRecentBirads || ''),
            width: 100,
        },
        {
            title: 'Result Notes',
            dataIndex: 'resultNotes',
            key: 'resultNotes',
            ellipsis: true,
            width: 180,
        },
        {
            title: 'Contact Date',
            dataIndex: 'mostRecentContactDate',
            key: 'mostRecentContactDate',
            render: (contactDate) => formatDate(contactDate),
            sorter: (a, b) => new Date(a.mostRecentContactDate) - new Date(b.mostRecentContactDate),
            width: 120,
        },
        {
            title: 'Contact Type',
            dataIndex: 'mostRecentContactType',
            key: 'mostRecentContactType',
            sorter: (a, b) => (a.mostRecentContactType || '').localeCompare(b.mostRecentContactType || ''),
            ellipsis: true,
            width: 140,
        },
        {
            title: 'Contacted By',
            dataIndex: 'mostRecentContactedBy',
            key: 'mostRecentContactedBy',
            sorter: (a, b) => (a.mostRecentContactedBy || '').localeCompare(b.mostRecentContactedBy || ''),
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Contact Notes',
            dataIndex: 'mostRecentContactNotes',
            key: 'mostRecentContactNotes',
            ellipsis: true,
            width: 180,
        },
        {
            title: 'Next F/U Date',
            dataIndex: 'nextFollowUpDate',
            key: 'nextFollowUpDate',
            render: (followUpDate) => formatDate(followUpDate),
            sorter: (a, b) => new Date(a.nextFollowUpDate) - new Date(b.nextFollowUpDate),
            width: 120,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 140,
            fixed: 'right',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewExam(row);
                        }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-1"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                                Globals.theme === 'dark' ? '#db2777' : '#be185d';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                                Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                        title="View exam"
                    >
                        View
                        <ExternalLink size={14} />
                    </button>
                    {row.mostRecentContactedById === userInfo?.userId && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTracking(row);
                            }}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{
                                backgroundColor: 'transparent',
                                color: Globals.theme === 'dark' ? '#f87171' : '#dc2626',
                                border: `1px solid ${Globals.theme === 'dark' ? '#f87171' : '#dc2626'}`,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    Globals.theme === 'dark' ? '#7f1d1d' : '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            title="Delete tracking action"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            )
        },
    ], [formatDate, formatMRN, handleViewExam, handleDeleteTracking]);

    // COLLAPSED STATE
    if (isCollapsed && !isExpanded) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                    Expand to view follow-up tracking
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="space-y-4">
            {/* Filter Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <div className="form-label">
                            Start Range
                        </div>
                        <DatePicker
                            onChange={(date) => handleNewDateChange('startDate', date)}
                            selectsStart
                            startDate={selectedDates.startDate}
                            endDate={selectedDates.endDate}
                            className="form-control-custom w-full"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            placeholderText="Start Date"
                            selected={selectedDates.startDate && Moment(selectedDates.startDate).toDate()}
                            customInput={<NumberFormat format="##/##/####" mask="_" />}
                        />
                    </div>
                    <div>
                        <div className="form-label">
                            End Range
                        </div>
                        <DatePicker
                            onChange={(date) => handleNewDateChange('endDate', date)}
                            selectsEnd
                            startDate={selectedDates.startDate}
                            endDate={selectedDates.endDate}
                            className="form-control-custom w-full"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            placeholderText="End Date"
                            selected={selectedDates.endDate && Moment(selectedDates.endDate).toDate()}
                            customInput={<NumberFormat format="##/##/####" mask="_" />}
                        />
                    </div>
                    <div>
                        <div className="form-label">
                            Facility <span className="color-pink">*</span>
                        </div>
                        {allFacilities.length === 1 ? (
                            <div className="form-control-custom">
                                {allFacilities[0].facilityModel.facilityName}
                            </div>
                        ) : (
                            <Select
                                mode="multiple"
                                maxTagCount={2}
                                allowClear
                                placeholder="Please select"
                                onChange={handleFacilityChange}
                                className="form-control-custom w-full"
                                bordered={false}
                                virtual={false}
                                value={selectedFacilities}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                <Option key="Select All" value="Select All">Select All</Option>
                                {allFacilities.map(fac => (
                                    <Option key={fac.facilityModel.facilityID} value={fac.facilityModel.facilityID}>
                                        {fac.facilityModel.facilityNumber
                                            ? `${fac.facilityModel.facilityNumber} - ${fac.facilityModel.facilityName}`
                                            : fac.facilityModel.facilityName}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </div>
                    <div>
                        <div className="form-label">
                            BIRADS <span className="color-pink">*</span>
                        </div>
                        <Select
                            mode="multiple"
                            maxTagCount={1}
                            allowClear
                            placeholder={availableBirads.length === 0 ? 'Select a facility first' : 'Please select'}
                            onChange={handleBiradChange}
                            className="form-control-custom w-full"
                            bordered={false}
                            virtual={false}
                            value={selectedBirads}
                            disabled={availableBirads.length === 0}
                        >
                            <Option key="Select All" value="Select All">Select All</Option>
                            {availableBirads.map(b => (
                                <Option key={b.birads} value={b.birads}>{b.birads}</Option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={queryParams && isFetching}
                        className="px-4 py-2 rounded-lg font-medium transition-colors"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            opacity: (queryParams && isFetching) ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (!(queryParams && isFetching)) {
                                e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#db2777' : '#be185d';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                    >
                        {(queryParams && isFetching) ? 'Loading...' : 'Generate Report'}
                    </button>
                </div>
            </form>

            {/* Results */}
            {queryParams && (
                <>
                    {results.length === 0 && !isLoading ? (
                        <div className="text-center py-8 text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                            No follow-up tracking records found for the selected filters
                        </div>
                    ) : (
                        <>
                            <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                                Results (<span style={{ color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b' }}>{results.length} record{results.length !== 1 ? 's' : ''}</span>)
                            </div>
                            <Table
                                columns={columns}
                                dataSource={results}
                                rowKey={(record) => record.patientResultId}
                                pagination={{
                                    pageSize: 50,
                                    pageSizeOptions: ['25', '50', '100'],
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
                                }}
                                size="small"
                                loading={isLoading}
                                scroll={{ y: 500 }}
                                virtual
                            />
                        </>
                    )}
                </>
            )}
        </div>

        {showAddExamPane && selectedPatient && (
            <PatientExamPane
                patientAge={calculateAge(selectedPatient?.dob)}
                patientInactive={false}
                patient={selectedPatient}
                showPane={showAddExamPane}
                examInfo={selectedPatientExam}
                examHistory={examResults}
                facility={facility}
                updateExamInfo={updateExamInfo}
                from='follow-up-tracking'
                hidePane={() => setShowAddExamPane(false)}
                sharedFollowUpIDs={sharedFollowUpIDs}
                getNewExamInfo={getNewExamInfo}
            />
        )}

        <Modal
            title="Delete Tracking Action"
            visible={showDeletionModal}
            onCancel={closeDeletionModal}
            onOk={handleConfirmDelete}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{
                danger: true,
                style: { backgroundColor: '#dc2626', borderColor: '#dc2626' }
            }}
        >
            <p>Are you sure you want to delete this tracking action?</p>
            {recordToDelete && (
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                    <p><strong>Patient:</strong> {recordToDelete.patientName}</p>
                    <p><strong>MRN:</strong> {recordToDelete.mrn}</p>
                    {recordToDelete.mostRecentContactDate && (
                        <p><strong>Contact Date:</strong> {formatDate(recordToDelete.mostRecentContactDate)}</p>
                    )}
                    {recordToDelete.mostRecentContactType && (
                        <p><strong>Contact Type:</strong> {recordToDelete.mostRecentContactType}</p>
                    )}
                </div>
            )}
            <p style={{ marginTop: '16px', color: '#dc2626' }}>
                <strong>Warning:</strong> This action cannot be undone.
            </p>
        </Modal>
        </>
    );
}
